import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../lib/utils';

export interface PaginationProps {
  current: number;
  total: number;
  pageSize: number;
  onChange?: (page: number) => void;
  showTotal?: (total: number, range: [number, number]) => React.ReactNode;
  className?: string;
}

const Pagination = React.forwardRef<HTMLElement, PaginationProps>(
  ({ current, total, pageSize, onChange, showTotal, className }, ref) => {
    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    const getRange = (): [number, number] => {
      if (total === 0) return [0, 0];
      const start = Math.min((current - 1) * pageSize + 1, total);
      const end = Math.min(current * pageSize, total);
      return [start, end];
    };

    const getPageNumbers = (): (number | 'ellipsis')[] => {
      const pages: (number | 'ellipsis')[] = [];

      if (totalPages <= 7) {
        for (let i = 1; i <= totalPages; i++) pages.push(i);
        return pages;
      }

      pages.push(1);
      if (current > 3) pages.push('ellipsis');

      const start = Math.max(2, current - 1);
      const end = Math.min(totalPages - 1, current + 1);
      for (let i = start; i <= end; i++) pages.push(i);

      if (current < totalPages - 2) pages.push('ellipsis');
      pages.push(totalPages);

      return pages;
    };

    const buttonClass =
      'inline-flex h-8 w-8 items-center justify-center rounded-md text-sm transition-colors hover:bg-light-gray disabled:pointer-events-none disabled:opacity-50';

    return (
      <nav
        ref={ref}
        role="navigation"
        aria-label="Pagination"
        className={cn('flex items-center gap-2', className)}
      >
        {showTotal && (
          <span className="mr-2 text-sm text-muted-foreground">
            {showTotal(total, getRange())}
          </span>
        )}

        <button
          className={buttonClass}
          onClick={() => onChange?.(current - 1)}
          disabled={current <= 1}
          aria-label="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        {getPageNumbers().map((page, i) =>
          page === 'ellipsis' ? (
            <span
              key={`ellipsis-${i}`}
              className="px-1 text-sm text-muted-foreground"
            >
              ...
            </span>
          ) : (
            <button
              key={page}
              className={cn(
                buttonClass,
                current === page && 'bg-primary text-white hover:bg-primary/90',
              )}
              onClick={() => onChange?.(page)}
              aria-current={current === page ? 'page' : undefined}
            >
              {page}
            </button>
          ),
        )}

        <button
          className={buttonClass}
          onClick={() => onChange?.(current + 1)}
          disabled={current >= totalPages}
          aria-label="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </nav>
    );
  },
);
Pagination.displayName = 'Pagination';

export { Pagination };
